import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import SEOHead from '../../components/common/SEOHead';
import HistoryList from './components/HistoryList';
import HistoryChart from './components/HistoryChart';
import StatsCircularCard from './components/StatsCircularCard';
import StatsCategorySelector from './components/StatsCategorySelector';
import CoursesTab from './components/CoursesTab';
import SubscriptionModal from './modals/SubscriptionModal';
import UpgradeBlockingModal from './modals/UpgradeBlockingModal';
import useLevelOverview from '../../hooks/dashboard/useLevelOverview';
import ShoppingBagIcon from './components/icons/ShoppingBagIcon';
import RecommendationIcon from '../../components/ui/icons/RecommendationIcon';
import ModeSelectionModal from './components/ModeSelectionModal';
import BannerStatsSlider from './components/BannerStatsSlider';
import ExamCard from './components/ExamCard';
import VuExamenCard from './components/VuExamenCard';
import LightningIcon from './components/icons/LightningIcon';
import ClockIcon from './components/icons/ClockIcon';
import CategoryButton from '../../components/common/CategoryButton';
import ExamQuestionHistoryModal from './modals/ExamQuestionHistoryModal';
import StatBadgeDetailModal from './components/StatBadgeDetailModal';
import KeywordExamSection from './components/KeywordExamSection';
import ErrorExamSection from './components/ErrorExamSection';
import RevisionExamSection from './components/RevisionExamSection';
import CustomExamHistorySection from './components/CustomExamHistorySection';
import SuggestVuQuestionModal from './components/SuggestVuQuestionModal';
import HoursRecord from '../account/HoursRecord';
import RecommendationLetter from '../account/RecommendationLetter';
import { useHelp } from '../../context/HelpContext';
import { userKey } from '../../utils/userStorage';
import useFavorites from '../../hooks/dashboard/useFavorites';
import { Star } from 'lucide-react';

// Build a CSS gradient with an elliptical dark curve at the bottom
const buildLevelGradient = (hex) => {
    if (!hex) return '#333';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    // Lighter version (+35%)
    const lr = Math.min(255, Math.round(r + (255 - r) * 0.35));
    const lg = Math.min(255, Math.round(g + (255 - g) * 0.35));
    const lb = Math.min(255, Math.round(b + (255 - b) * 0.35));
    // Dark version (-75%)
    const dr = Math.round(r * 0.25);
    const dg = Math.round(g * 0.25);
    const db = Math.round(b * 0.25);
    const light = `rgb(${lr},${lg},${lb})`;
    const mid   = `rgb(${r},${g},${b})`;
    const dark  = `rgb(${dr},${dg},${db})`;
    // Elliptical dark gradient at bottom + subtle diagonal base
    return [
        `radial-gradient(100% 150% at 50% 150%, ${dark} 0%, transparent 80%)`,
        `linear-gradient(135deg, ${light} 0%, ${mid} 100%)`
    ].join(', ');
};

const LevelOverview = () => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const { isFavoriteExam, toggleFavoriteExam } = useFavorites();
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [historyModalExam, setHistoryModalExam] = useState(null);
    const [loadingHistoryExamId, setLoadingHistoryExamId] = useState(null);
    const [loadingAnnexeId, setLoadingAnnexeId] = useState(null);
    const [activeBadgeModal, setActiveBadgeModal] = useState(null); // 'days' | 'progression' | 'comprehension' | 'rewards' | null
    const [showHoursRecordModal, setShowHoursRecordModal] = useState(false);
    const [showRecommendationModal, setShowRecommendationModal] = useState(false);
    // Track which tooltip is open: 'examId-progression' or 'examId-comprehension' or null
    const [openTooltip, setOpenTooltip] = useState(null);
    // Joint pagination state for HistoryChart and HistoryList
    const [historyPage, setHistoryPage] = useState(0);
    // Toggle to show/hide exam card stats (persisted in localStorage)
    const [showExamStats, setShowExamStats] = useState(() => {
        try {
            const saved = localStorage.getItem(userKey('nizus_show_exam_stats'));
            return saved !== null ? JSON.parse(saved) : true;
        } catch {
            return true;
        }
    });
    const categoryGridRef = useRef(null);
    const topRef = useRef(null);

    // Reset loading state once modal is rendered (modal handles its own internal loading)
    useEffect(() => {
        if (historyModalExam) {
            // Small delay to ensure the spinner is visible before resetting
            const timer = setTimeout(() => setLoadingHistoryExamId(null), 150);
            return () => clearTimeout(timer);
        }
    }, [historyModalExam]);

    // Toggle exam stats visibility and persist to localStorage
    const toggleExamStats = useCallback(() => {
        setShowExamStats(prev => {
            const newValue = !prev;
            try {
                localStorage.setItem(userKey('nizus_show_exam_stats'), JSON.stringify(newValue));
            } catch {}
            return newValue;
        });
    }, []);
    const {
        levelId,
        theme,
        activeTab,
        setActiveTab,
        selectedCategory,
        setSelectedCategory,
        statsCategory,
        setStatsCategory,
        showModeModal,
        setShowModeModal,
        error,
        currentModule,
        hasCategories,
        statsDetail,
        filteredHistory,
        keywordsExam,
        regularExams,
        showIncompleteOnly,
        setShowIncompleteOnly,
        activeExams,
        activeSessions,
        lastOpenedExamId,
        lastReviewedHistoryId,
        lastActiveCustomId,
        stats,
        loadStats,
        handleExamAction,
        startNewExam,
        handleStartKeywordExam,
        handleReviewSession,
        handleReviewCustomSession,
        handleRestartCustomSession,
        hasModules,
        selectedModule,
        setSelectedModule,
        modules,
        categoryRestoredRef,
        upgradeModal,
        closeUpgradeModal,
        isAdminPreview,
        newAchievementCodes,
        dismissNewCodes
    } = useLevelOverview();

    // Vu-examen intro popup state
    const [vuExamenIntroExam, setVuExamenIntroExam] = useState(null);
    const [showSuggestModal, setShowSuggestModal] = useState(false);

    // Wrapper: show intro popup for vu-examen on first launch
    const handleExamActionWithVuIntro = useCallback((exam) => {
        if (exam.isVuExamen && !activeSessions[exam.id]) {
            // New session (not resuming) — show intro popup
            setVuExamenIntroExam(exam);
            return;
        }
        handleExamAction(exam);
    }, [handleExamAction, activeSessions]);

    // Scroll to top on level change when no saved module/group preference exists
    useEffect(() => {
        if (!levelId) return;
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            const prefs = raw ? JSON.parse(raw) : {};
            const hasSavedModule = !!prefs.lastCourseModules?.[levelId];
            const hasSavedCategory = prefs.lastCategories?.[levelId] && prefs.lastCategories[levelId] !== 'complet';
            if (!hasSavedModule && !hasSavedCategory) {
                topRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
            }
        } catch {
            topRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    }, [levelId]);

    // Reset history pagination when category or level changes
    useEffect(() => {
        setHistoryPage(0);
    }, [selectedCategory, levelId]);

    // Reset categoryRestoredRef when stats load (no longer auto-scrolling to avoid hiding banner)
    useEffect(() => {
        if (statsDetail && categoryRestoredRef.current) {
            categoryRestoredRef.current = false;
        }
    }, [statsDetail]);

    // Declare active help page based on current tab (so "? Aide" button triggers the right tour)
    const { startTour, hasSeenPage, helpPrefs, isPrefsSynced, setActiveHelpPage } = useHelp();
    const TAB_HELP_MAP = { stats: 'stats-tab', custom: 'custom-tab', courses: 'courses-tab' };
    useEffect(() => {
        setActiveHelpPage(TAB_HELP_MAP[activeTab] || 'level-overview');
        return () => setActiveHelpPage(null);
    }, [activeTab, setActiveHelpPage]);

    // Auto-trigger help tour on first visit
    useEffect(() => {
        if (!isPrefsSynced) return;
        if (helpPrefs.globalEnabled && helpPrefs.autoTrigger && !hasSeenPage('level-overview') && statsDetail) {
            const timer = setTimeout(() => startTour('level-overview'), 800);
            return () => clearTimeout(timer);
        }
    }, [isPrefsSynced, statsDetail, helpPrefs.globalEnabled, helpPrefs.autoTrigger, hasSeenPage]);

    // Auto-trigger stats-tab help tour on first visit to stats tab
    useEffect(() => {
        if (!isPrefsSynced) return;
        if (activeTab === 'stats' && helpPrefs.globalEnabled && helpPrefs.autoTrigger && !hasSeenPage('stats-tab') && statsDetail) {
            const timer = setTimeout(() => startTour('stats-tab'), 800);
            return () => clearTimeout(timer);
        }
    }, [isPrefsSynced, activeTab, statsDetail, helpPrefs.globalEnabled, helpPrefs.autoTrigger, hasSeenPage]);

    // Auto-trigger custom-tab help tour on first visit to custom tab
    useEffect(() => {
        if (!isPrefsSynced) return;
        if (activeTab === 'custom' && helpPrefs.globalEnabled && helpPrefs.autoTrigger && !hasSeenPage('custom-tab')) {
            const timer = setTimeout(() => startTour('custom-tab'), 800);
            return () => clearTimeout(timer);
        }
    }, [isPrefsSynced, activeTab, helpPrefs.globalEnabled, helpPrefs.autoTrigger, hasSeenPage]);

    // Auto-trigger courses-tab help tour on first visit to courses tab
    useEffect(() => {
        if (!isPrefsSynced) return;
        if (activeTab === 'courses' && helpPrefs.globalEnabled && helpPrefs.autoTrigger && !hasSeenPage('courses-tab')) {
            const timer = setTimeout(() => startTour('courses-tab'), 800);
            return () => clearTimeout(timer);
        }
    }, [isPrefsSynced, activeTab, helpPrefs.globalEnabled, helpPrefs.autoTrigger, hasSeenPage]);

    // Helper to safe render text
    const getLocalizedText = (textObj) => {
        if (!textObj) return '';
        if (typeof textObj === 'string') return textObj;
        return textObj[language] || textObj['en'] || '';
    };

    const categories = [
        { id: 'droitAerien', label: { fr: 'Droit Aérien', en: 'Air Law' } },
        { id: 'meteo', label: { fr: 'Météorologie', en: 'Meteorology' } },
        { id: 'connaissance', label: { fr: 'Connaissance G.', en: 'Gen. Knowledge' } },
        { id: 'navigation', label: { fr: 'Navigation', en: 'Navigation' } },
        { id: 'complet', label: { fr: 'Examen complet', en: 'Full Exam' } },
    ];

    // Auto-dismiss new achievement codes toast after 12 seconds
    useEffect(() => {
        if (newAchievementCodes.length > 0) {
            const timer = setTimeout(dismissNewCodes, 12000);
            return () => clearTimeout(timer);
        }
    }, [newAchievementCodes, dismissNewCodes]);

    const isBannerLight = false;
    const bannerTextTitleSpec = isBannerLight ? 'text-gray-900' : 'text-white';
    const bannerTextValueSpec = isBannerLight ? 'text-gray-900' : 'text-white';
    const bannerTextLabelSpec = isBannerLight ? 'text-gray-800' : 'text-white/85';
    const bannerIconSpec = isBannerLight ? 'text-gray-800' : 'text-white/75';

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="bg-[#121212] border border-red-500/20 rounded-3xl p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{language === 'fr' ? 'Oups !' : 'Oops!'}</h3>
                    <p className="text-gray-400 mb-8">{error}</p>
                    <button
                        onClick={loadStats}
                        className="px-8 py-3 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-colors"
                    >
                        {language === 'fr' ? 'Réessayer' : 'Retry'}
                    </button>
                </div>
            </div>
        );
    }

    if (!currentModule || !statsDetail) {
        return <div className="p-12 text-center text-gray-500">{language === 'fr' ? 'Chargement...' : 'Loading...'}</div>;
    }

    const levelTitle = currentModule.titleObj?.[language] || currentModule.title || '';

    return (
        <div ref={topRef} className="min-h-screen bg-transparent">
            <SEOHead
                title={levelTitle || undefined}
                noindex
            />

            {/* Achievement Code Toast */}
            {newAchievementCodes.length > 0 && (
                <div className="px-4 md:px-8 max-w-7xl mx-auto pt-6">
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 p-4 shadow-lg shadow-amber-500/20 animate-[fadeIn_0.5s_ease-out]">
                        <div className="absolute inset-0 opacity-20 pointer-events-none"
                            style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.3) 50%)', backgroundSize: '4px 100%' }} />
                        <div className="relative z-10 flex items-center gap-4">
                            <div className="flex-shrink-0 p-2 bg-white/20 rounded-xl">
                                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5a5 5 0 0110 0v2a5 5 0 01-10 0zM12 22a9 9 0 110-18 9 9 0 010 18z" />
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-white font-bold text-sm">
                                    {language === 'fr' ? 'Code promo débloqué !' : 'Promo code unlocked!'}
                                </p>
                                {newAchievementCodes.map((ac, i) => (
                                    <p key={i} className="text-white/90 text-sm mt-0.5">
                                        <span className="font-mono font-bold bg-white/20 px-2 py-0.5 rounded">{ac.code}</span>
                                        <span className="ml-2">(-{ac.percentage}%)</span>
                                    </p>
                                ))}
                            </div>
                            <button onClick={dismissNewCodes} className="flex-shrink-0 p-1 hover:bg-white/20 rounded-lg transition">
                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Header Banner */}
            <div className="pt-6 px-4 md:px-8 max-w-7xl mx-auto">
                <div
                    className="relative overflow-hidden rounded-[2rem] shadow-2xl border border-white/5"
                    style={{
                        background: buildLevelGradient(currentModule.newBackgroundColor),
                        minHeight: '260px'
                    }}
                >

                    {/* Content Container */}
                    <div className="relative z-10 p-8 flex flex-col justify-between h-full min-h-[260px]">
                        {/* Top Section */}
                        <div className="flex items-center justify-between">
                            <div
                                className="px-4 py-1.5 rounded-full text-white font-black text-[10px] uppercase tracking-wider shadow-md"
                                style={{ backgroundColor: currentModule.newBackgroundColor, filter: 'brightness(0.85)' }}
                            >
                                {getLocalizedText(currentModule.titleObj || currentModule.title)}
                            </div>


                            {levelId !== 'pstar' && (
                                <div className="flex flex-col md:flex-row items-end md:items-center gap-1.5 md:gap-2">
                                    {/* Recommendation Letter button (if enabled for this level — visible even with expired subscription) */}
                                    {currentModule.hasRecommendation && (
                                        <button
                                            onClick={() => setShowRecommendationModal(true)}
                                            className={`px-5 py-2 bg-black/40 backdrop-blur-md hover:bg-black/50 border border-white/10 ${bannerTextTitleSpec} rounded-xl font-black text-xs transition flex items-center gap-2 shadow-lg group`}
                                        >
                                            <div className="p-1 bg-white/10 rounded-lg group-hover:bg-white/20">
                                                <RecommendationIcon className="w-3.5 h-3.5" />
                                            </div>
                                            {language === 'fr' ? 'Recommandation' : 'Recommendation'}
                                        </button>
                                    )}

                                    <button
                                        onClick={() => setShowSubscriptionModal(true)}
                                        className={`px-5 py-2 bg-black/40 backdrop-blur-md hover:bg-black/50 border border-white/10 ${bannerTextTitleSpec} rounded-xl font-black text-xs transition flex items-center gap-2 shadow-lg group`}
                                    >
                                        <div className="p-1 bg-white/10 rounded-lg group-hover:bg-white/20">
                                            <ShoppingBagIcon className="w-3.5 h-3.5" />
                                        </div>
                                        {user?.activeLevels?.[levelId]
                                            ? (language === 'fr' ? 'Prolonger' : 'Extend')
                                            : user?.everPurchasedLevels?.includes(levelId)
                                                ? (language === 'fr' ? 'Redémarrer' : 'Restart')
                                                : (language === 'fr' ? 'Démarrer' : 'Start')
                                        }
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Middle Section: Overview Title */}
                        <div className="mt-6">
                            <h1 className={`text-3xl font-extrabold ${bannerTextTitleSpec} drop-shadow-sm`}>{language === 'fr' ? 'Aperçu' : 'Overview'}</h1>
                        </div>

                        {/* Bottom Section: Stats Cards */}
                        <BannerStatsSlider
                            language={language}
                            levelId={levelId}
                            activeTab={activeTab}
                            statsDetail={statsDetail}
                            isBannerLight={isBannerLight}
                            bannerTextValueSpec={bannerTextValueSpec}
                            bannerTextLabelSpec={bannerTextLabelSpec}
                            bannerIconSpec={bannerIconSpec}
                            onBadgeClick={setActiveBadgeModal}
                        />
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="bg-gray-50 dark:bg-black min-h-screen py-6 md:py-8 lg:py-10 transition-colors duration-300">
                {/* Tabs - scrollable on mobile */}
                <div data-help="tab-navigation" className="flex items-center mb-12 border-b border-gray-200 dark:border-white/5 max-w-7xl mx-auto px-4 md:px-8 overflow-x-auto overflow-y-hidden no-scrollbar">
                    {/* Left group: Exams + Stats + Custom with small separators */}
                    <div className="flex items-center flex-shrink-0">
                        <button
                            onClick={() => setActiveTab('exams')}
                            className={`pb-3 px-2 font-bold text-xs transition-colors relative uppercase tracking-widest cursor-pointer whitespace-nowrap
                                ${activeTab === 'exams' ? 'text-blue-600 dark:text-white' : 'text-gray-400 dark:text-gray-500'}
                            `}
                        >
                            {language === 'fr' ? 'Examen' : 'Exams'}
                            {activeTab === 'exams' && (
                                <div className="absolute bottom-0 left-0 w-full h-[3px] bg-blue-600 dark:bg-white rounded-t-full shadow-[0_0_10px_rgba(59,130,246,0.5)] dark:shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
                            )}
                        </button>

                        {/* Small separator */}
                        <div className="mx-3 h-3 w-px bg-gray-300 dark:bg-white/20 self-center -mb-1" />

                        <button
                            onClick={() => setActiveTab('stats')}
                            className={`pb-3 px-2 font-bold text-xs transition-colors relative uppercase tracking-widest cursor-pointer whitespace-nowrap
                                ${activeTab === 'stats' ? 'text-blue-600 dark:text-white' : 'text-gray-400 dark:text-gray-500'}
                            `}
                        >
                            <span className="hidden sm:inline">{language === 'fr' ? 'Statistiques' : 'Statistics'}</span>
                            <span className="sm:hidden">{language === 'fr' ? 'Stats' : 'Stats'}</span>
                            {activeTab === 'stats' && (
                                <div className="absolute bottom-0 left-0 w-full h-[3px] bg-blue-600 dark:bg-white rounded-t-full shadow-[0_0_10px_rgba(59,130,246,0.5)] dark:shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
                            )}
                        </button>

                        {/* Small separator */}
                        <div className="mx-3 h-3 w-px bg-gray-300 dark:bg-white/20 self-center -mb-1" />

                        <button
                            onClick={() => setActiveTab('custom')}
                            className={`pb-3 px-2 font-bold text-xs transition-colors relative uppercase tracking-widest cursor-pointer whitespace-nowrap
                                ${activeTab === 'custom' ? 'text-blue-600 dark:text-white' : 'text-gray-400 dark:text-gray-500'}
                            `}
                        >
                            <span className="hidden sm:inline">{language === 'fr' ? 'Sur mesure' : 'Custom'}</span>
                            <span className="sm:hidden">{language === 'fr' ? 'Sur mesure' : 'Custom'}</span>
                            {activeTab === 'custom' && (
                                <div className="absolute bottom-0 left-0 w-full h-[3px] bg-blue-600 dark:bg-white rounded-t-full shadow-[0_0_10px_rgba(59,130,246,0.5)] dark:shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
                            )}
                        </button>
                    </div>

                    {/* Large separator + Courses tab */}
                    {(currentModule.hasCourses || statsDetail?.hasCourseAvailable) && (
                        <div className="flex items-center flex-shrink-0">
                            <div className="mx-4 md:mx-8 h-8 w-[2px] bg-gray-300 dark:bg-white/15 self-center -mb-1 rounded-full" />
                            <button
                                onClick={() => setActiveTab('courses')}
                                className={`pb-3 px-2 font-bold text-xs transition-colors relative uppercase tracking-widest cursor-pointer whitespace-nowrap
                                    ${activeTab === 'courses' ? 'text-blue-600 dark:text-white' : 'text-gray-400 dark:text-gray-500'}
                                `}
                            >
                                {language === 'fr' ? 'Cours' : 'Courses'}
                                {activeTab === 'courses' && (
                                    <div className="absolute bottom-0 left-0 w-full h-[3px] bg-blue-600 dark:bg-white rounded-t-full shadow-[0_0_10px_rgba(59,130,246,0.5)] dark:shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
                                )}
                            </button>
                        </div>
                    )}
                </div>

                {/* Exams Tab Content */}
                {activeTab === 'exams' && (
                    <div className="flex flex-col gap-12 max-w-7xl mx-auto pb-24 px-4 md:px-8 relative">
                        {/* Mots-clés Card */}
                        {keywordsExam && !selectedCategory && (
                            <div className="space-y-4">
                                <h2 className="text-lg font-black text-gray-400 dark:text-white ml-2 text-xs uppercase tracking-widest opacity-60 dark:opacity-40">Mots-clés</h2>
                                <div data-help="keyword-exam" className={`bg-white dark:bg-[#121212] rounded-2xl p-6 border border-gray-200 dark:border-white/5 flex items-center justify-between group transition-all shadow-sm dark:shadow-none ${
                                    keywordsExam.requiresUpgrade
                                        ? 'opacity-60 grayscale pointer-events-none hover:opacity-80 hover:grayscale-0'
                                        : 'hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
                                }`}>
                                    <div className="flex items-center gap-6">
                                        <div className="w-16 h-16 rounded-2xl bg-[#2DDB81] flex items-center justify-center shadow-lg relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
                                            <div className="w-10 h-5 bg-white/20 rounded-full border border-white/30 flex items-center px-1 relative">
                                                <div className="w-4 h-3 bg-white rounded-md shadow-sm" />
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                                                {keywordsExam.requiresUpgrade && (
                                                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                    </svg>
                                                )}
                                                {getLocalizedText(keywordsExam.title)}
                                                {keywordsExam.requiresUpgrade && (
                                                    <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">
                                                        Premium
                                                    </span>
                                                )}
                                            </h3>
                                            <p className="text-xs text-gray-500 max-w-sm">{getLocalizedText(keywordsExam.description)}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleExamAction(keywordsExam)}
                                        className={`px-6 py-2.5 rounded-xl font-bold text-sm shadow-lg transition flex items-center gap-2 ${
                                            keywordsExam.requiresUpgrade
                                                ? 'pointer-events-auto text-amber-700 dark:text-amber-300 bg-gradient-to-r from-amber-100 to-amber-200 dark:from-amber-500/20 dark:to-amber-600/20 border border-amber-300 dark:border-amber-500/30'
                                                : 'text-white bg-[#007AFF] hover:bg-[#0062CC] hover:shadow-blue-500/20'
                                        }`}
                                    >
                                        {keywordsExam.requiresUpgrade ? (
                                            <>
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                                {language === 'fr' ? 'Débloquer' : 'Unlock'}
                                            </>
                                        ) : (
                                            <>
                                                {activeSessions[keywordsExam.id] && (
                                                    activeSessions[keywordsExam.id].mode === 'flash'
                                                        ? <LightningIcon className="w-4 h-4" />
                                                        : <ClockIcon className="w-4 h-4" />
                                                )}
                                                {activeSessions[keywordsExam.id]
                                                    ? (language === 'fr' ? 'Continuer' : 'Continue')
                                                    : (language === 'fr' ? 'Commencer' : 'Start')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Category Grid - Horizontal slider on mobile, grid on desktop */}
                        {hasCategories && (
                            <div data-help="category-selector" className="-mx-6 md:mx-0 scroll-mt-6" ref={categoryGridRef}>
                                <div className="flex md:grid md:grid-cols-5 gap-3 md:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none no-scrollbar px-6 md:px-0 pb-2 md:pb-0">
                                    {categories.map((cat) => {
                                        const catStats = cat.id === 'complet' ? stats?.all : stats?.[cat.id];
                                        // Compute average progression from exams in this category
                                        const catExams = cat.id === 'complet'
                                            ? activeExams.filter(e => e.category !== 'keywords')
                                            : activeExams.filter(e => e.category === cat.id);
                                        const avgProgress = catExams.length > 0
                                            ? Math.round(catExams.reduce((sum, e) => sum + (e.progression || 0), 0) / catExams.length)
                                            : 0;
                                        return (
                                        <div key={cat.id} className="flex-shrink-0 md:flex-shrink snap-center w-[140px] md:w-auto">
                                            <CategoryButton
                                                category={cat}
                                                isSelected={selectedCategory === cat.id}
                                                onClick={(catId) => { if (selectedCategory !== catId) setSelectedCategory(catId); }}
                                                language={language}
                                                progress={avgProgress}
                                                time={catStats?.totalTime}
                                            />
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Exam List */}
                        <div data-help="exam-cards" className="">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-black text-gray-400 dark:text-white ml-2 text-xs uppercase tracking-widest opacity-60 dark:opacity-40">
                                    {categories.find(c => c.id === selectedCategory)?.label[language] ||
                                        (language === 'fr' ? 'Examens' : 'Exams')
                                    }
                                </h2>
                                <div className="flex items-center gap-2">
                                {/* Toggle "Favoris" filter */}
                                <button
                                    onClick={() => setShowFavoritesOnly(v => !v)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                        showFavoritesOnly
                                            ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-500/30'
                                            : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
                                    }`}
                                    title={showFavoritesOnly
                                        ? (language === 'fr' ? 'Afficher tous les examens' : 'Show all exams')
                                        : (language === 'fr' ? 'Afficher uniquement les favoris' : 'Show favorites only')
                                    }
                                >
                                    <Star size={14} className={showFavoritesOnly ? 'fill-current' : ''} />
                                    <span className="hidden sm:inline">
                                        {language === 'fr' ? 'Favoris' : 'Favorites'}
                                    </span>
                                </button>
                                {/* Toggle "Non terminé" filter */}
                                <button
                                    onClick={() => setShowIncompleteOnly(v => !v)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                        showIncompleteOnly
                                            ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-500/30'
                                            : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
                                    }`}
                                    title={showIncompleteOnly
                                        ? (language === 'fr' ? 'Afficher tous les examens' : 'Show all exams')
                                        : (language === 'fr' ? 'Afficher uniquement les non terminés' : 'Show only incomplete')
                                    }
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                    </svg>
                                    <span className="hidden sm:inline">
                                        {language === 'fr' ? 'Non terminé' : 'Incomplete'}
                                    </span>
                                </button>
                                {/* Toggle stats visibility */}
                                <button
                                    onClick={toggleExamStats}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                        showExamStats
                                            ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-500/30'
                                            : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
                                    }`}
                                    title={showExamStats
                                        ? (language === 'fr' ? 'Masquer les statistiques' : 'Hide statistics')
                                        : (language === 'fr' ? 'Afficher les statistiques' : 'Show statistics')
                                    }
                                >
                                    {showExamStats ? (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    )}
                                    <span className="hidden sm:inline">
                                        {showExamStats
                                            ? (language === 'fr' ? 'Stats' : 'Stats')
                                            : (language === 'fr' ? 'Stats' : 'Stats')
                                        }
                                    </span>
                                </button>
                                </div>
                            </div>
                            <div className="space-y-4">
                                {(() => {
                                    const displayedExams = showFavoritesOnly
                                        ? regularExams.filter(exam => isFavoriteExam(exam.id))
                                        : regularExams;
                                    return displayedExams.length > 0 ? (
                                        displayedExams.map((exam, idx) => (
                                            exam.isVuExamen ? (
                                            <VuExamenCard
                                                key={idx}
                                                exam={exam}
                                                language={language}
                                                activeSessions={activeSessions}
                                                isActive={lastOpenedExamId === exam.id}
                                                isFavorite={isFavoriteExam(exam.id)}
                                                onToggleFavorite={toggleFavoriteExam}
                                                getLocalizedText={getLocalizedText}
                                                onExamAction={handleExamActionWithVuIntro}
                                                onSuggest={() => setShowSuggestModal(true)}
                                            />
                                            ) : (
                                            <ExamCard
                                                key={idx}
                                                exam={exam}
                                                language={language}
                                                showExamStats={showExamStats}
                                                activeSessions={activeSessions}
                                                isActive={lastOpenedExamId === exam.id}
                                                isFavorite={isFavoriteExam(exam.id)}
                                                onToggleFavorite={toggleFavoriteExam}
                                                openTooltip={openTooltip}
                                                setOpenTooltip={setOpenTooltip}
                                                loadingHistoryExamId={loadingHistoryExamId}
                                                loadingAnnexeId={loadingAnnexeId}
                                                setLoadingAnnexeId={setLoadingAnnexeId}
                                                levelId={levelId}
                                                getLocalizedText={getLocalizedText}
                                                onExamAction={handleExamAction}
                                                onHistoryClick={(exam) => {
                                                    setLoadingHistoryExamId(exam.id);
                                                    setHistoryModalExam(exam);
                                                }}
                                            />
                                            )
                                        ))
                                    ) : (
                                        <div className="text-center py-8 text-gray-500 bg-white dark:bg-gray-900/30 rounded-xl border border-gray-100 dark:border-gray-800 text-sm">
                                            {showFavoritesOnly
                                                ? (language === 'fr' ? 'Aucun examen favori dans cette catégorie. Cliquez sur l\u2019étoile d\u2019un examen pour l\u2019ajouter à vos favoris.' : 'No favorite exams in this category. Click the star on an exam to add it to your favorites.')
                                                : showIncompleteOnly
                                                    ? (language === 'fr' ? 'Tous les examens de cette catégorie sont terminés !' : 'All exams in this category are completed!')
                                                    : (language === 'fr' ? 'Aucun examen trouvé pour cette catégorie.' : 'No exams found for this category.')
                                            }
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                )}

                {/* Stats Tab Content */}
                {activeTab === 'stats' && (
                    <div className="flex flex-col gap-16 max-w-7xl mx-auto pb-24 px-4 md:px-8 relative">
                        {/* Category Selector for Stats */}
                        {hasCategories && (
                            <StatsCategorySelector
                                categories={categories}
                                selectedCategory={selectedCategory}
                                onSelect={setSelectedCategory}
                                language={language}
                            />
                        )}

                        {/* Stats Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 mb-12 auto-rows-max lg:auto-rows-[32rem]">
                            {/* Card 1: Stats Summary (Circular + Footer) */}
                            <div data-help="stats-summary"><StatsCircularCard
                                statsDetail={statsDetail}
                                language={language}
                                categoryLabel={selectedCategory
                                    ? categories.find(c => c.id === selectedCategory)?.label[language]
                                    : (language === 'fr' ? 'Moyenne générale' : 'General Average')
                                }
                            /></div>

                            {/* Card 2: History Chart */}
                            <div data-help="stats-chart"><HistoryChart
                                history={filteredHistory}
                                language={language}
                                handleReviewSession={handleReviewSession}
                                page={historyPage}
                                setPage={setHistoryPage}
                                itemsPerPage={8}
                                minScore={statsDetail?.minScore || 75}
                                isAdminPreview={isAdminPreview}
                                lastReviewedId={lastReviewedHistoryId}
                            /></div>
                        </div>

                        {/* History List Section */}
                        <div data-help="stats-history"><HistoryList
                            history={filteredHistory}
                            language={language}
                            currentModule={currentModule}
                            handleReviewSession={handleReviewSession}
                            page={historyPage}
                            setPage={setHistoryPage}
                            itemsPerPage={8}
                            minScore={statsDetail?.minScore || 75}
                            isAdminPreview={isAdminPreview}
                            lastReviewedId={lastReviewedHistoryId}
                        /></div>

                    </div>
                )}

                {/* Custom Exams Tab Content */}
                {activeTab === 'custom' && (
                    <div className="flex flex-col gap-12 max-w-7xl mx-auto pb-24 px-4 md:px-8 relative">
                        <ErrorExamSection
                            key={levelId}
                            levelId={levelId}
                            language={language}
                            onStartExam={handleStartKeywordExam}
                            activeExams={activeExams}
                            requiresUpgrade={!(stats?.all?.hasExamAccess ?? true)}
                            onUpgrade={() => setShowSubscriptionModal(true)}
                        />
                        <KeywordExamSection
                            key={`kw-${levelId}`}
                            levelId={levelId}
                            language={language}
                            onStartExam={handleStartKeywordExam}
                            requiresUpgrade={!(stats?.all?.hasExamAccess ?? true)}
                            onUpgrade={() => setShowSubscriptionModal(true)}
                        />
                        <RevisionExamSection
                            key={`rev-${levelId}`}
                            levelId={levelId}
                            language={language}
                            onStartExam={handleStartKeywordExam}
                            activeExams={activeExams}
                            requiresUpgrade={!(stats?.all?.hasExamAccess ?? true)}
                            onUpgrade={() => setShowSubscriptionModal(true)}
                        />
                        <CustomExamHistorySection
                            key={`hist-${levelId}`}
                            levelId={levelId}
                            language={language}
                            onReviewSession={handleReviewCustomSession}
                            onRestartSession={handleRestartCustomSession}
                            lastActiveId={lastActiveCustomId}
                        />
                    </div>
                )}

                {/* Courses Tab Content */}
                {activeTab === 'courses' && (
                    <CoursesTab
                        levelId={levelId}
                        language={language}
                        hasCourseAccess={statsDetail?.hasCourseAccess ?? false}
                        onPurchase={() => setShowSubscriptionModal(true)}
                    />
                )}
            </div>

            {/* Vu-examen intro popup */}
            {vuExamenIntroExam && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4" onClick={() => setVuExamenIntroExam(null)}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <img src="/img/application/vu_icone.svg" alt="" className="w-10 h-10" />
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {language === 'fr' ? 'Vu à l\'examen' : 'Seen on the exam'}
                            </h3>
                        </div>
                        {vuExamenIntroExam.vuExamenEmpty ? (
                            <>
                                <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed space-y-3">
                                    {language === 'fr' ? (
                                        <p>Il n'y a aucune question « Vu à l'examen » disponible pour ce niveau pour le moment. Aidez-nous en suggérant des questions que vous avez vues lors de votre examen chez Transports Canada. Vous obtiendrez 10 % de rabais pour chaque nouvelle question que nous ajouterons grâce à vos informations.</p>
                                    ) : (
                                        <p>There are no "Seen on the exam" questions available for this level yet. Help us by suggesting questions you saw during your Transport Canada exam. You'll get 10% off for each new question we add based on your input.</p>
                                    )}
                                </div>
                                <button
                                    onClick={() => {
                                        setVuExamenIntroExam(null);
                                        setShowSuggestModal(true);
                                    }}
                                    className="w-full mt-6 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 transition shadow-lg shadow-red-500/20"
                                >
                                    {language === 'fr' ? 'Suggérer une question' : 'Suggest a question'}
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed space-y-3">
                                    {language === 'fr' ? (
                                        <>
                                            <p>Les questions qui suivent ont été fournies par des utilisateurs ayant récemment passé leur examen chez Transports Canada (TC).</p>
                                            <p>Comme beaucoup de nos utilisateurs, aidez-nous à proposer des questions-types de TC et obtenez 10 % de rabais pour chaque nouvelle question que nous ajouterons à nos examens grâce à vos informations. Plus vous donnez de détails (question, choix de réponses, etc.), plus vous aurez de chances de contribuer à la création d'une nouvelle question, et donc d'obtenir des rabais.</p>
                                        </>
                                    ) : (
                                        <>
                                            <p>The following questions have been submitted by users who recently wrote the Transport Canada (TC) examination. The questions will remain here for a while until they are added to a new full examination.</p>
                                            <p>Join the growing number of users who help us by suggesting TC sample questions and get 10% off for each new question that we add to our exams based on your input. The more details you give us (questions, multiple choice options, etc.), the better your odds of helping us write a new question, and the better your odds of getting a discount.</p>
                                        </>
                                    )}
                                </div>
                                <div className="flex gap-3 mt-6">
                                    <button
                                        onClick={() => {
                                            const exam = vuExamenIntroExam;
                                            setVuExamenIntroExam(null);
                                            handleExamAction(exam);
                                        }}
                                        className="flex-1 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 transition shadow-lg shadow-red-500/20"
                                    >
                                        {language === 'fr' ? 'Commencer' : 'Start'}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setVuExamenIntroExam(null);
                                            setShowSuggestModal(true);
                                        }}
                                        className="py-2.5 px-5 rounded-xl font-bold text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20 hover:bg-red-50 dark:hover:bg-red-500/10 transition"
                                    >
                                        {language === 'fr' ? 'Suggérer' : 'Suggest'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Suggest Vu-Examen Question Modal */}
            {showSuggestModal && (
                <SuggestVuQuestionModal
                    levelId={levelId}
                    language={language}
                    onClose={() => setShowSuggestModal(false)}
                />
            )}

            {/* Mode Selection Modal */}
            {showModeModal && (
                <ModeSelectionModal
                    language={language}
                    onSelectMode={startNewExam}
                    onClose={() => setShowModeModal(null)}
                />
            )}

            {/* Subscription Modal */}
            {showSubscriptionModal && (
                <SubscriptionModal
                    levelVar={levelId}
                    onClose={() => setShowSubscriptionModal(false)}
                />
            )}

            {/* Upgrade Blocking Modal (Free tier) */}
            <UpgradeBlockingModal
                isOpen={upgradeModal.isOpen}
                onClose={closeUpgradeModal}
                levelVar={levelId}
                contentType="exam"
                onUpgrade={() => setShowSubscriptionModal(true)}
            />

            {/* Question History Modal */}
            {historyModalExam && (
                <ExamQuestionHistoryModal
                    exam={historyModalExam}
                    language={language}
                    onClose={() => {
                        setHistoryModalExam(null);
                        setLoadingHistoryExamId(null);
                    }}
                />
            )}

            {/* Stat Badge Detail Modal */}
            {activeBadgeModal && (
                <StatBadgeDetailModal
                    type={activeBadgeModal}
                    statsDetail={statsDetail}
                    activeTab={activeTab}
                    levelColor={currentModule.newBackgroundColor}
                    language={language}
                    levelId={levelId}
                    onClose={() => setActiveBadgeModal(null)}
                />
            )}

            {/* Hours Record Modal (GLIDE) */}
            {showHoursRecordModal && (
                <HoursRecord levelVar={levelId} onClose={() => setShowHoursRecordModal(false)} />
            )}

            {/* Recommendation Letter Modal */}
            {showRecommendationModal && (
                <RecommendationLetter levelVar={levelId} onClose={() => setShowRecommendationModal(false)} />
            )}
        </div>
    );
};

export default LevelOverview;
